import { useRef, useState } from "react";

import UploadDropzone from "~/components/UploadDropzone";
import { useUploadResource } from "~/resource/feature/use-upload-resource";

import { AddFilesModal } from "./feature/add-files-modal";
import UploadSpecModal from "./ui/upload-spec-modal";
import { UploadContext } from "./upload-context";
import { useFileUpload } from "./use-file-upload";

/**
 * App-wide file upload. Owns the add-files / spec modals and drag-and-drop overlay so uploading is
 * reachable from anywhere (sidebar, search, file details) without first selecting a file. Mounted
 * once above the authenticated routes; trigger the modal with `useUpload().openAddFiles()`.
 */
export function UploadProvider({ children }: { children: React.ReactNode }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addFilesModalOpen, setAddFilesModalOpen] = useState(false);
  const {
    isModalOpen,
    filesToUpload,
    handleFilesDropped,
    handleCloseModal,
    handleUploadButtonClick,
    handleFileInputChange,
  } = useFileUpload();
  const { uploadFiles } = useUploadResource();

  return (
    <UploadContext.Provider value={{ openAddFiles: () => setAddFilesModalOpen(true) }}>
      <UploadDropzone onFilesDropped={handleFilesDropped} overlayDescription="Files will be uploaded to the workspace">
        {children}
      </UploadDropzone>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleFileInputChange}
        style={{ display: "none" }}
        aria-hidden="true"
      />
      {isModalOpen && filesToUpload ? (
        <UploadSpecModal
          files={filesToUpload}
          open={isModalOpen}
          setModalOpen={handleCloseModal}
          onFilesUploaded={uploadFiles}
        />
      ) : null}
      <AddFilesModal
        open={addFilesModalOpen}
        onOpenChange={setAddFilesModalOpen}
        onUploadClick={() => handleUploadButtonClick(fileInputRef)}
        onConnectUpload={uploadFiles}
      />
    </UploadContext.Provider>
  );
}
